import type { AppScreen, AppScreenColumn, ChampNatif, FieldReference } from '@taki/data-model';
import {
  CHAMPS_DE_TRACE,
  FAMILLE_PERSONNES,
  PREFIXE_CHAMP_SOCLE,
  TYPE_ANNUAIRE_PAR_UID,
  champsNatifs,
  estFamille,
} from '@taki/data-model';

/**
 * LE SOCLE TYPE SES CHAMPS NATIFS LUI-MEME (chantier `champ-personne`).
 *
 * CE QUI FONDE CE MODULE. Une colonne native (`socle:workerId`) porte un `type`
 * DECLARE, et ce type vit dans deux fichiers qui vieillissent : le
 * `taki.screens.json` du modele, et l'INSTANCE d'une org, recopiee le jour de sa
 * personnalisation et jamais re-ecrite d'elle-meme. Le jour ou le socle apprend
 * que `workerId` designe une PERSONNE, l'instance deja en base dit encore
 * `text`, et l'ecran offrirait une case de texte libre sur un identifiant.
 *
 * LE SCHEMA DE LA FAMILLE FAIT DONC AUTORITE : une colonne native dont le genre
 * est `personne` (marque `personneId()`, `familles.ts`) devient `type: 'personne'` ;
 * un `z.date()` devient `type: 'datetime'`, meme dans une instance ancienne. Les champs de
 * TRACE (`createdBy`, `updatedBy`, `createdAt`, `updatedAt`), poses par le SDK
 * et absents de `champsNatifs`, prennent le genre de `CHAMPS_DE_TRACE` : les
 * deux premiers designent une personne PAR UID (c'est ce que le SDK y ecrit),
 * les deux autres sont des instants.
 *
 * CE QU'IL NE TOUCHE PAS : tout le reste de la colonne (libelle, habillage
 * declare, reference declaree). Une reference declaree sur un champ personne
 * choisit le MOT affiche (`socle:email`), elle reste ; `apps:check` et Zod ont
 * deja refuse celle qui viserait une autre famille.
 *
 * PURE, et appelee par `assemblerEcran` en tete : le modele et l'instance
 * passent par la, donc par le meme typage.
 */

/** La reference d'une trace : l'annuaire des personnes, range par uid. */
const REFERENCE_PAR_UID: FieldReference = Object.freeze({
  family: FAMILLE_PERSONNES,
  type: TYPE_ANNUAIRE_PAR_UID,
});

/** L'habillage d'un instant de trace quand la declaration n'en nomme aucun. */
const HABILLAGE_INSTANT = 'dateHeure';

/**
 * LES CLEFS NATIVES DE GENRE `personne` ou `date` des jeux que l'ecran monte. Une famille
 * inconnue est sautee ici : `sdk.famille` la refusera au montage, avec son nom,
 * et ce n'est pas a ce typage de lever a sa place.
 */
function clefsNatives(ecran: AppScreen): { personnes: ReadonlySet<string>; instants: ReadonlySet<string> } {
  const personnes = new Set<string>();
  const instants = new Set<string>();
  for (const porte of ecran.portes) {
    const famille = porte.jeu?.family;
    if (famille === undefined || !estFamille(famille)) continue;
    for (const champ of champsNatifs(famille)) {
      if (champ.genre === 'personne') personnes.add(champ.key);
      if (champ.genre === 'date') instants.add(champ.key);
    }
  }
  return { personnes, instants };
}

/** La colonne d'une trace, typee par `CHAMPS_DE_TRACE`. */
function colonneDeTrace(colonne: AppScreenColumn, trace: ChampNatif): AppScreenColumn {
  if (trace.genre === 'personne') {
    return {
      ...colonne,
      type: 'personne',
      reference: colonne.reference ?? REFERENCE_PAR_UID,
    };
  }
  return {
    ...colonne,
    type: 'datetime',
    habillage: colonne.habillage ?? HABILLAGE_INSTANT,
  };
}

/** L'ecran dont les colonnes natives de personne, d'instant ou de trace sont typees par le socle. */
export function qualifierLesNatifs(ecran: AppScreen): AppScreen {
  const colonnes = ecran.colonnes;
  if (colonnes === undefined || colonnes.length === 0) return ecran;
  const { personnes, instants } = clefsNatives(ecran);
  let change = false;
  const qualifiees = colonnes.map((colonne) => {
    if (!colonne.champ.startsWith(PREFIXE_CHAMP_SOCLE)) return colonne;
    const clef = colonne.champ.slice(PREFIXE_CHAMP_SOCLE.length);
    const trace = CHAMPS_DE_TRACE.find((champ) => champ.key === clef);
    if (trace !== undefined) {
      change = true;
      return colonneDeTrace(colonne, trace);
    }
    if (personnes.has(clef) && colonne.type !== 'personne') {
      change = true;
      /* UN PLACEHOLDER D'ADRESSE (« prenom.nom@exemple.lu ») n'a plus de sens :
         la case devient un selecteur, et l'exemple gris mentirait. */
      const { placeholder: _exemple, ...reste } = colonne;
      return { ...reste, type: 'personne' as const };
    }
    if (instants.has(clef) && colonne.type !== 'datetime') {
      change = true;
      return { ...colonne, type: 'datetime' as const };
    }
    return colonne;
  });
  return change ? { ...ecran, colonnes: qualifiees } : ecran;
}
